function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`.toUpperCase()
}

function hslToRgb(
  h: number,
  s: number,
  l: number,
): { r: number; g: number; b: number } {
  const sat = s / 100
  const light = l / 100
  const c = (1 - Math.abs(2 * light - 1)) * sat
  const hp = h / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r1 = 0
  let g1 = 0
  let b1 = 0
  if (hp >= 0 && hp < 1) [r1, g1, b1] = [c, x, 0]
  else if (hp < 2) [r1, g1, b1] = [x, c, 0]
  else if (hp < 3) [r1, g1, b1] = [0, c, x]
  else if (hp < 4) [r1, g1, b1] = [0, x, c]
  else if (hp < 5) [r1, g1, b1] = [x, 0, c]
  else [r1, g1, b1] = [c, 0, x]
  const m = light - c / 2
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 }
}

// Read a Tailwind/shadcn HSL token (e.g. "--primary" stored as "220 65% 48%")
// from the document root and convert to hex. Returns null in non-DOM contexts
// or if the var is missing/unparseable.
export function resolvePortalThemeHex(varName: string): string | null {
  if (typeof window === 'undefined') return null
  const raw = getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim()
  if (!raw) return null
  const match = raw.match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/)
  if (!match) return null
  const h = parseFloat(match[1])
  const s = parseFloat(match[2])
  const l = parseFloat(match[3])
  const { r, g, b } = hslToRgb(h, s, l)
  return rgbToHex(r, g, b)
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const match = /^#([0-9a-fA-F]{6})$/.exec(hex.trim())
  if (!match) return null
  const v = parseInt(match[1], 16)
  return { r: (v >> 16) & 0xff, g: (v >> 8) & 0xff, b: v & 0xff }
}

function srgbToLinear(channel: number): number {
  const c = channel / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

export function getPortalRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex)
  if (!rgb) return 0.5
  const r = srgbToLinear(rgb.r)
  const g = srgbToLinear(rgb.g)
  const b = srgbToLinear(rgb.b)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

// WCAG-AA target: pick black when the accent is light, white when dark.
export function getPortalContrastForeground(hex: string): 'black' | 'white' {
  return getPortalRelativeLuminance(hex) > 0.5 ? 'black' : 'white'
}

// WCAG contrast ratio between two hex colors (1.0 – 21.0).
export function getPortalContrastRatio(a: string, b: string): number {
  const la = getPortalRelativeLuminance(a)
  const lb = getPortalRelativeLuminance(b)
  const lighter = Math.max(la, lb)
  const darker = Math.min(la, lb)
  return (lighter + 0.05) / (darker + 0.05)
}

// Pick a readable foreground for the active sidebar nav row: prefer the
// accent (so the row pops) when it has AA contrast against the sidebar bg;
// otherwise fall back to the sidebar's foreground color.
export function getPortalSidebarActiveFg(
  accentHex: string | null | undefined,
  sidebarHex: string | null | undefined,
): string {
  if (!accentHex) return sidebarHex ? getPortalContrastForeground(sidebarHex) : 'currentColor'
  if (!sidebarHex) return accentHex
  const ratio = getPortalContrastRatio(accentHex, sidebarHex)
  return ratio >= 4.5 ? accentHex : getPortalContrastForeground(sidebarHex)
}
